import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate, fmtMoney, formatApiErrorDetail, triggerFileDownload, downloadWithToast } from "@/lib/apiClient";
import { devLog } from "@/lib/devLog";
import PaymentMilestonesPanel from "@/components/billing/PaymentMilestonesPanel";
import AbstractExportButton from "@/components/billing/AbstractExportButton";
import BOQSectionsSummary from "@/components/billing/BOQSectionsSummary";
import BOQWizard from "@/components/billing/BOQWizard";
import RABillSheet from "@/components/billing/RABillSheet";
import MSCreateDialog from "@/components/billing/MSCreateDialog";
import ChallanCreateDialog from "@/components/billing/ChallanCreateDialog";
import BulkExportDialog from "@/components/billing/BulkExportDialog";
import RABillComparisonSheet from "@/components/billing/RABillComparisonSheet";
import { PageHeader, EmptyState, BcRibbonGroup, BcRibbonBtn, StatusBadge } from "@/components/Shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  Plus, Upload, FloppyDisk, Trash, Files, ChartBar, Sparkle, ArrowsClockwise,
  FilePdf, FileXls, Receipt, ClipboardText, ArrowRight, CheckCircle, Warning,
  FileText, Scales, FileZip,
} from "@phosphor-icons/react";
import { toast } from "sonner";

// -------- Helpers --------
function KPI({ label, value, tone = "slate", testid }) {
  const cls = {
    slate: "bg-white border-slate-200 text-slate-900",
    orange: "bg-[#EA580C] text-white border-[#EA580C]",
    dark: "bg-[#0F172A] text-white border-[#0F172A]",
    red: "bg-red-600 text-white border-red-600",
    emerald: "bg-emerald-600 text-white border-emerald-600",
  }[tone];
  return (
    <div className={`border ${cls} rounded-sm p-4`} data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-[0.14em] font-semibold ${tone === "slate" ? "text-slate-500" : "text-white/70"}`}>{label}</div>
      <div className="font-display text-[22px] font-bold font-mono-num mt-1">{value ?? "—"}</div>
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div>
      <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1 block">{label}</Label>
      {children}
      {hint && <div className="text-[10.5px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}
// ============================================================================
// Main hub
// ============================================================================
export default function RABilling() {
  const [stats, setStats] = useState(null);
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/ra-bills-stats"); setStats(data); } catch (e) { devLog.warn(e); }
    setTick((t) => t + 1);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="ra-billing-page">
      <PageHeader
        title="Client BOQ → RA Bills"
        module="Sales · Construction Billing"
        subtitle="Client-authoritative Bill-of-Quantities driven RA (Running Account) billing with milestone splits (Supply · Install · T&C), ITC ledger per payment terms, signed measurement sheets & client delivery challans. Editable at every stage."
        ribbon={
          <BcRibbonGroup label="Process">
            <BcRibbonBtn icon={ArrowsClockwise} label="Refresh" onClick={load} testid="rab-refresh" />
          </BcRibbonGroup>
        }
      />
      <div className="p-6 space-y-5">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <KPI label="Client BOQs" tone="dark" value={stats.boq_count} testid="kpi-boq" />
            <KPI label="RA Bills" tone="slate" value={stats.ra_count} testid="kpi-ra" />
            <KPI label="Draft RAs" tone={stats.draft_count > 0 ? "orange" : "slate"} value={stats.draft_count} testid="kpi-draft" />
            <KPI label="Awaiting Payment" tone={stats.pending_payment_count > 0 ? "orange" : "slate"} value={stats.pending_payment_count} testid="kpi-pending" />
            <KPI label="Measurement Sheets" tone="slate" value={stats.measurement_sheet_count} testid="kpi-ms" />
            <KPI label="Pending ₹" tone="emerald" value={fmtMoney(stats.total_pending_amount)} testid="kpi-pending-amount" />
          </div>
        )}
        <Tabs defaultValue="boqs" className="w-full" data-testid="rab-tabs">
          <TabsList className="flex flex-wrap h-auto">
            <TabsTrigger value="boqs" data-testid="tab-boqs">Client BOQs</TabsTrigger>
            <TabsTrigger value="ra" data-testid="tab-ra">RA Bills</TabsTrigger>
            <TabsTrigger value="measurements" data-testid="tab-ms">Measurement Sheets</TabsTrigger>
            <TabsTrigger value="challans" data-testid="tab-dc">Client Challans</TabsTrigger>
          </TabsList>
          <TabsContent value="boqs" className="mt-5"><BOQListPanel onChange={load} refreshTick={tick} /></TabsContent>
          <TabsContent value="ra" className="mt-5"><RABillsPanel onChange={load} refreshTick={tick} /></TabsContent>
          <TabsContent value="measurements" className="mt-5"><MSPanel refreshTick={tick} /></TabsContent>
          <TabsContent value="challans" className="mt-5"><ChallanPanel refreshTick={tick} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ============================================================================
// Panel: BOQ list + editor
// ============================================================================
function BOQListPanel({ onChange, refreshTick }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);      // {} for create, {id: ...} to edit
  const [uploadOpen, setUploadOpen] = useState(false);
  const navigate = useNavigate();

  const [wizardOpen, setWizardOpen] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/boqs"); setRows(data || []); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }, []);
  useEffect(() => { load(); }, [load, refreshTick]);

  const remove = async (id) => {
    if (!window.confirm("Delete this BOQ? All RA drafts, sheets and challans linked to it will also be removed.")) return;
    try { await api.delete(`/boqs/${id}`); toast.success("Deleted"); load(); onChange?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">
          Client BOQs · {rows.length}
        </div>
        <div className="flex gap-2">
          <Button size="sm" className="rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 h-8" onClick={() => setUploadOpen(true)} data-testid="boq-upload-btn"><Upload size={13} className="mr-1" /> Upload Excel</Button>
          <Button size="sm" className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white h-8" onClick={() => setWizardOpen(true)} data-testid="boq-wizard-btn"><Sparkle size={13} className="mr-1" /> New Client BOQ</Button>
        </div>
      </div>
      {rows.length === 0 ? (
        <EmptyState title="No Client BOQs yet" description="Click New Client BOQ to upload the client PO/WO + BOQ Excel in one flow, or Upload Excel directly. Each item can have a milestone-based payment split (Supply 85% · Install 10% · T&C 5% by default)." />
      ) : (
        <div className="bc-card border border-slate-200 rounded-sm overflow-hidden">
          <Table className="bc-list">
            <TableHeader>
              <TableRow>
                <TableHead>BOQ #</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Work Order</TableHead>
                <TableHead className="text-right">WO Value</TableHead>
                <TableHead className="text-center">Items</TableHead>
                <TableHead className="text-center">RA Bills</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-32"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`boq-row-${r.id}`}>
                  <TableCell className="font-mono text-[11.5px] font-semibold">{r.boq_no}</TableCell>
                  <TableCell className="font-semibold">{r.boq_title}</TableCell>
                  <TableCell>{r.client_name}</TableCell>
                  <TableCell className="font-mono text-[11.5px]">{r.work_order_no || "—"}</TableCell>
                  <TableCell className="text-right font-mono-num font-semibold">{fmtMoney(r.work_order_value)}</TableCell>
                  <TableCell className="text-center font-mono-num">{(r.items || []).length}</TableCell>
                  <TableCell className="text-center font-mono-num">{r.ra_bill_count ?? 0}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" onClick={() => setEditing({ id: r.id })} data-testid={`boq-edit-${r.id}`}>Open</Button>
                      <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px] text-red-600 border-red-200 hover:bg-red-50" onClick={() => remove(r.id)} data-testid={`boq-del-${r.id}`}><Trash size={11} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {editing && <BOQEditorSheet
        initial={editing.id ? null : (editing.initial || {})}
        boqId={editing.id}
        onClose={() => setEditing(null)}
        onSaved={() => {
          load();
          onChange?.();
          // iter55: if there are queued tower BOQs left, open the next one
          if (editing.queue && editing.queue.length > 0) {
            const [next, ...rest] = editing.queue;
            setEditing({ initial: next, queue: rest });
          } else {
            setEditing(null);
          }
        }}
      />}
      {uploadOpen && <BOQUploadDialog onClose={() => setUploadOpen(false)} onImported={(preview) => { setUploadOpen(false); setEditing({ initial: preview }); }} />}
      {wizardOpen && (
        <BOQWizard
          onClose={() => setWizardOpen(false)}
          onReady={(payload) => {
            setWizardOpen(false);
            // iter55: payload may be a single preview or an array (split-by-tower)
            if (Array.isArray(payload)) {
              // Queue all previews — the current editor opens the first, next
              // one opens after save (via a small state queue)
              const [first, ...rest] = payload;
              setEditing({ initial: first, queue: rest });
            } else {
              setEditing({ initial: payload });
            }
          }}
        />
      )}
    </div>
  );
}

// ---- BOQ Editor Sheet ----
function BOQEditorSheet({ boqId, initial, onClose, onSaved }) {
  const [boq, setBoq] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reconRows, setReconRows] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);

  useEffect(() => {
    (async () => {
      if (boqId) {
        setLoading(true);
        try {
          const { data } = await api.get(`/boqs/${boqId}`);
          setBoq(data);
          const rr = await api.get(`/boqs/${boqId}/reconcile`);
          setReconRows(rr.data?.rows || []);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
        finally { setLoading(false); }
      } else {
        // New with optional preview
        const draft = initial || {
          boq_title: "",
          client_name: "",
          contractor_name: "Celsius",
          default_supply_pct: 85,
          default_install_pct: 10,
          default_tnc_pct: 5,
          retention_pct: 5,
          tds_pct: 1,
          default_gst_rate: 18,
          is_interstate: false,
          items: [],
        };
        setBoq(draft);
      }
    })();
  }, [boqId, initial]);

  const patch = (k, v) => setBoq((b) => ({ ...b, [k]: v }));
  const patchItem = (idx, k, v) => setBoq((b) => ({ ...b, items: b.items.map((it, i) => i === idx ? { ...it, [k]: v } : it) }));

  // Load list of projects for the project selector
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    (async () => { try { const { data } = await api.get("/projects"); setProjects(data || []); } catch (e) { devLog.warn(e); } })();
  }, []);

  // Auto-fill WO fields from a project when linked
  const applyProjectWO = useCallback(async (projectId, opts = { silent: false }) => {
    if (!projectId) return;
    try {
      const { data: p } = await api.get(`/projects/${projectId}`);
      setBoq((b) => ({
        ...b,
        project_id: projectId,
        client_name: p.client_name || b.client_name,
        client_address: p.client_address || b.client_address,
        client_gstin: p.client_gstin || b.client_gstin,
        client_state: p.client_state || b.client_state,
        site: p.site || p.location || b.site,
        work_order_no: p.work_order_no || b.work_order_no,
        work_order_date: p.work_order_date || b.work_order_date,
        work_order_value: p.work_order_value || b.work_order_value,
        agreement_no: p.agreement_no || b.agreement_no,
        name_of_work: p.name_of_work || b.name_of_work,
        sub_project: p.sub_project || b.sub_project,
        sub_head: p.sub_head || b.sub_head,
        contractor_name: p.contractor_name || b.contractor_name,
        contractor_pan: p.contractor_pan || b.contractor_pan,
        contractor_gstin: p.contractor_gstin || b.contractor_gstin,
        contractor_state: p.contractor_state || b.contractor_state,
        is_interstate: (typeof p.is_interstate === "boolean") ? p.is_interstate : b.is_interstate,
        wo_attachment_id: p.wo_attachment_id || b.wo_attachment_id,
        boq_title: b.boq_title || p.name || "",
      }));
      if (!opts.silent) toast.success("Work Order details loaded from project — review & save");
    } catch (e) { if (!opts.silent) toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }, []);

  const addItem = () => setBoq((b) => ({ ...b, items: [...(b.items || []), {
    sr_no: String((b.items?.length || 0) + 1),
    description: "",
    unit: "Nos",
    contract_qty: 0,
    rate: 0,
    supply_pct: b.default_supply_pct ?? 85,
    install_pct: b.default_install_pct ?? 10,
    tnc_pct: b.default_tnc_pct ?? 5,
    gst_rate: b.default_gst_rate ?? 18,
    hsn: "",
    notes: "",
  }] }));
  const removeItem = (idx) => setBoq((b) => ({ ...b, items: b.items.filter((_, i) => i !== idx) }));

  const totalAmount = useMemo(
    () => (boq?.items || []).reduce((s, it) => s + (Number(it.contract_qty) || 0) * (Number(it.rate) || 0), 0),
    [boq]
  );

  const save = async () => {
    if (!boq?.boq_title) return toast.error("BOQ title is required");
    if (!boq?.client_name) return toast.error("Client name is required");
    setLoading(true);
    try {
      // Strip transient wizard-only fields before sending to API
      const { _pending_wo_file, _pending_excel_file, ...payload } = boq;
      let savedBoq;
      if (boqId) {
        const { data } = await api.put(`/boqs/${boqId}`, payload);
        savedBoq = data;
        toast.success("BOQ updated");
      } else {
        const { data } = await api.post("/boqs", payload);
        savedBoq = data;
        toast.success("Client BOQ created");
      }
      // Upload any pending PO/WO or BOQ Excel file from the wizard
      if (savedBoq?.id && (_pending_wo_file || _pending_excel_file)) {
        try {
          if (_pending_wo_file) {
            const fd = new FormData();
            fd.append("file", _pending_wo_file);
            await api.post(`/attachments/upload?parent_type=boq&parent_id=${savedBoq.id}&category=client_po`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          }
          if (_pending_excel_file) {
            const fd = new FormData();
            fd.append("file", _pending_excel_file);
            await api.post(`/attachments/upload?parent_type=boq&parent_id=${savedBoq.id}&category=boq`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          }
          toast.success("PO/WO + BOQ files attached");
        } catch (upErr) { toast.warning("Files were pending but upload failed — you can retry from Attachments"); }
      }
      onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  if (!boq) return null;

  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-[min(1200px,100vw)] sm:max-w-none overflow-y-auto" data-testid="boq-editor-sheet">
        <SheetHeader className="mb-4">
          <SheetTitle className="font-display flex items-center gap-2">
            <ClipboardText size={18} className="text-[#EA580C]" />
            {boqId ? `Edit BOQ · ${boq.boq_no || ""}` : "New BOQ"}
          </SheetTitle>
        </SheetHeader>
        <Tabs defaultValue="header">
          <TabsList>
            <TabsTrigger value="header" data-testid="boq-tab-header">Header</TabsTrigger>
            <TabsTrigger value="items" data-testid="boq-tab-items">Items ({(boq.items || []).length})</TabsTrigger>
            <TabsTrigger value="sections" data-testid="boq-tab-sections">Sub-heads</TabsTrigger>
            <TabsTrigger value="ra" data-testid="boq-tab-ra">RA Bills</TabsTrigger>
            <TabsTrigger value="recon" data-testid="boq-tab-recon">Reconciliation</TabsTrigger>
          </TabsList>
          <TabsContent value="header" className="mt-4">
            <div className="border border-[#EA580C]/30 bg-[#EA580C]/5 rounded-sm p-3 mb-4 flex items-center gap-3 flex-wrap" data-testid="boq-project-linker">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-[#EA580C] whitespace-nowrap">Link Project ▸</div>
              <div className="flex-1 min-w-[260px]">
                <Select value={boq.project_id || ""} onValueChange={(v) => { patch("project_id", v); applyProjectWO(v); }}>
                  <SelectTrigger data-testid="boq-project-select"><SelectValue placeholder="Pick a project — WO details will auto-load" /></SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.client_name}{p.work_order_no ? ` · ${p.work_order_no}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {boq.project_id && <Button size="sm" variant="outline" className="rounded-sm h-8 text-[11.5px]" onClick={() => applyProjectWO(boq.project_id)} data-testid="boq-reload-wo"><ArrowsClockwise size={13} className="mr-1" /> Reload WO from Project</Button>}
              {boq.wo_attachment_id && (
                <Button size="sm" variant="outline" className="rounded-sm h-8 text-[11.5px]" title="Download the client Work Order file" onClick={() => triggerFileDownload(`/attachments/${boq.wo_attachment_id}/download`, "WorkOrder.pdf")} data-testid="boq-wo-download">
                  <FilePdf size={13} className="mr-1" /> Download WO file
                </Button>
              )}
              <div className="text-[11px] text-slate-500 basis-full">Whenever WO details change on the project, click <b>Reload WO from Project</b> to refresh — or edit fields below manually. New BOQs auto-load WO on project selection.</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="BOQ Title *"><Input value={boq.boq_title || ""} onChange={(e) => patch("boq_title", e.target.value)} data-testid="boq-title" /></Field>
              <Field label="Client Name *"><Input value={boq.client_name || ""} onChange={(e) => patch("client_name", e.target.value)} data-testid="boq-client" /></Field>
              <Field label="Client State"><Input value={boq.client_state || ""} onChange={(e) => patch("client_state", e.target.value)} /></Field>
              <Field label="Client Address"><Input value={boq.client_address || ""} onChange={(e) => patch("client_address", e.target.value)} /></Field>
              <Field label="Client GSTIN"><Input value={boq.client_gstin || ""} onChange={(e) => patch("client_gstin", e.target.value)} className="font-mono" /></Field>
              <Field label="Site"><Input value={boq.site || ""} onChange={(e) => patch("site", e.target.value)} /></Field>
              <Field label="Work Order #"><Input value={boq.work_order_no || ""} onChange={(e) => patch("work_order_no", e.target.value)} className="font-mono" /></Field>
              <Field label="WO Date"><Input type="date" value={boq.work_order_date || ""} onChange={(e) => patch("work_order_date", e.target.value)} /></Field>
              <Field label="WO Value (₹)"><Input type="number" value={boq.work_order_value || 0} onChange={(e) => patch("work_order_value", Number(e.target.value) || 0)} className="font-mono-num text-right" /></Field>
              <Field label="Name of Work"><Input value={boq.name_of_work || ""} onChange={(e) => patch("name_of_work", e.target.value)} /></Field>
              <Field label="Sub Project"><Input value={boq.sub_project || ""} onChange={(e) => patch("sub_project", e.target.value)} /></Field>
              <Field label="Sub Head"><Input value={boq.sub_head || "MEP"} onChange={(e) => patch("sub_head", e.target.value)} /></Field>
              <Field label="Agreement No."><Input value={boq.agreement_no || ""} onChange={(e) => patch("agreement_no", e.target.value)} className="font-mono" /></Field>
              <Field label="Contractor"><Input value={boq.contractor_name || "Celsius"} onChange={(e) => patch("contractor_name", e.target.value)} /></Field>
              <Field label="Contractor PAN"><Input value={boq.contractor_pan || ""} onChange={(e) => patch("contractor_pan", e.target.value)} className="font-mono" /></Field>
              <Field label="Contractor GSTIN"><Input value={boq.contractor_gstin || ""} onChange={(e) => patch("contractor_gstin", e.target.value)} className="font-mono" /></Field>
              <Field label="Contractor State"><Input value={boq.contractor_state || ""} onChange={(e) => patch("contractor_state", e.target.value)} /></Field>
              <Field label="Interstate?" hint="Sets IGST vs CGST+SGST for RA bills">
                <Select value={boq.is_interstate ? "yes" : "no"} onValueChange={(v) => patch("is_interstate", v === "yes")}>
                  <SelectTrigger data-testid="boq-interstate"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="no">No · CGST + SGST</SelectItem><SelectItem value="yes">Yes · IGST</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Default Supply %"><Input type="number" value={boq.default_supply_pct ?? 85} onChange={(e) => patch("default_supply_pct", Number(e.target.value) || 0)} className="font-mono-num text-right" data-testid="boq-supply-pct" /></Field>
              <Field label="Default Install %"><Input type="number" value={boq.default_install_pct ?? 10} onChange={(e) => patch("default_install_pct", Number(e.target.value) || 0)} className="font-mono-num text-right" data-testid="boq-install-pct" /></Field>
              <Field label="Default T&C %"><Input type="number" value={boq.default_tnc_pct ?? 5} onChange={(e) => patch("default_tnc_pct", Number(e.target.value) || 0)} className="font-mono-num text-right" data-testid="boq-tnc-pct" /></Field>
              <Field label="Retention %"><Input type="number" value={boq.retention_pct ?? 5} onChange={(e) => patch("retention_pct", Number(e.target.value) || 0)} className="font-mono-num text-right" data-testid="boq-retention-pct" /></Field>
              <Field label="TDS %"><Input type="number" value={boq.tds_pct ?? 1} onChange={(e) => patch("tds_pct", Number(e.target.value) || 0)} className="font-mono-num text-right" data-testid="boq-tds-pct" /></Field>
              <Field label="Default GST %"><Input type="number" value={boq.default_gst_rate ?? 18} onChange={(e) => patch("default_gst_rate", Number(e.target.value) || 0)} className="font-mono-num text-right" /></Field>
            </div>
            <PaymentMilestonesPanel boq={boq} setBoq={setBoq} />
            <div className="mt-3">
              <Field label="Notes"><Textarea rows={2} value={boq.notes || ""} onChange={(e) => patch("notes", e.target.value)} /></Field>
            </div>
          </TabsContent>
          <TabsContent value="items" className="mt-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">
                BOQ Items · Total ₹ <span className="font-mono-num text-slate-900 font-bold">{fmtMoney(totalAmount)}</span>
              </div>
              <Button size="sm" className="h-8 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700" onClick={addItem} data-testid="boq-add-item"><Plus size={13} className="mr-1" /> Add item</Button>
            </div>
            <div className="border border-slate-200 rounded-sm overflow-x-auto">
              <Table className="bc-list">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">SR</TableHead>
                    <TableHead className="w-64">Description</TableHead>
                    <TableHead className="w-20">Unit</TableHead>
                    <TableHead className="w-20">HSN</TableHead>
                    <TableHead className="w-24 text-right">Qty</TableHead>
                    <TableHead className="w-28 text-right">Rate</TableHead>
                    <TableHead className="w-28 text-right">Amount</TableHead>
                    <TableHead className="w-20 text-right">Supply%</TableHead>
                    <TableHead className="w-20 text-right">Install%</TableHead>
                    <TableHead className="w-20 text-right">T&C%</TableHead>
                    <TableHead className="w-20 text-right">GST%</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    const rows = [];
                    let lastSection = null;
                    let sectionSubtotal = 0;
                    let sectionStartIdx = -1;
                    const items = boq.items || [];
                    const flushSubtotal = () => {
                      if (lastSection !== null && sectionStartIdx >= 0) {
                        rows.push(
                          <TableRow key={`sub-${sectionStartIdx}`} className="bg-slate-100">
                            <TableCell colSpan={6} className="text-right text-[10.5px] font-semibold uppercase tracking-widest text-slate-700 py-1.5" data-testid={`section-subtotal-label-${sectionStartIdx}`}>Sub-total · {lastSection}</TableCell>
                            <TableCell className="text-right font-mono-num font-bold text-slate-900" data-testid={`section-subtotal-${sectionStartIdx}`}>{fmtMoney(sectionSubtotal)}</TableCell>
                            <TableCell colSpan={5}></TableCell>
                          </TableRow>
                        );
                      }
                    };
                    items.forEach((it, idx) => {
                      const sec = (it.section || "").trim();
                      if (sec !== (lastSection || "")) {
                        flushSubtotal();
                        lastSection = sec || null;
                        sectionStartIdx = idx;
                        sectionSubtotal = 0;
                        if (sec) {
                          rows.push(
                            <TableRow key={`sec-${idx}`} className="bg-[#0F172A] hover:bg-[#0F172A]">
                              <TableCell colSpan={12} className="py-1.5 text-white text-[11px] font-semibold uppercase tracking-widest" data-testid={`section-header-${idx}`}>
                                <span className="text-[#EA580C] mr-2">§</span> {sec}
                              </TableCell>
                            </TableRow>
                          );
                        }
                      }
                      const amount = (Number(it.contract_qty) || 0) * (Number(it.rate) || 0);
                      sectionSubtotal += amount;
                      rows.push(
                        <TableRow key={`item-${idx}`}>
                          <TableCell><Input value={it.sr_no || ""} onChange={(e) => patchItem(idx, "sr_no", e.target.value)} className="h-7 w-14 font-mono text-[11.5px]" /></TableCell>
                          <TableCell><Input value={it.description || ""} onChange={(e) => patchItem(idx, "description", e.target.value)} className="h-7" /></TableCell>
                          <TableCell><Input value={it.unit || ""} onChange={(e) => patchItem(idx, "unit", e.target.value)} className="h-7 w-20 uppercase text-[11.5px]" /></TableCell>
                          <TableCell><Input value={it.hsn || ""} onChange={(e) => patchItem(idx, "hsn", e.target.value)} className="h-7 w-20 font-mono text-[11px]" /></TableCell>
                          <TableCell><Input type="number" value={it.contract_qty || 0} onChange={(e) => patchItem(idx, "contract_qty", Number(e.target.value) || 0)} className="h-7 text-right font-mono-num w-24" /></TableCell>
                          <TableCell><Input type="number" value={it.rate || 0} onChange={(e) => patchItem(idx, "rate", Number(e.target.value) || 0)} className="h-7 text-right font-mono-num w-28" /></TableCell>
                          <TableCell className="text-right font-mono-num font-semibold">{fmtMoney(amount)}</TableCell>
                          <TableCell><Input type="number" value={it.supply_pct ?? 85} onChange={(e) => patchItem(idx, "supply_pct", Number(e.target.value) || 0)} className="h-7 text-right font-mono-num w-20" /></TableCell>
                          <TableCell><Input type="number" value={it.install_pct ?? 10} onChange={(e) => patchItem(idx, "install_pct", Number(e.target.value) || 0)} className="h-7 text-right font-mono-num w-20" /></TableCell>
                          <TableCell><Input type="number" value={it.tnc_pct ?? 5} onChange={(e) => patchItem(idx, "tnc_pct", Number(e.target.value) || 0)} className="h-7 text-right font-mono-num w-20" /></TableCell>
                          <TableCell><Input type="number" value={it.gst_rate ?? 18} onChange={(e) => patchItem(idx, "gst_rate", Number(e.target.value) || 0)} className="h-7 text-right font-mono-num w-20" /></TableCell>
                          <TableCell><Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => removeItem(idx)}><Trash size={12} /></Button></TableCell>
                        </TableRow>
                      );
                    });
                    flushSubtotal();
                    return rows;
                  })()}
                </TableBody>
              </Table>
              {(boq.items || []).length === 0 && (
                <div className="p-8 text-center text-slate-400 text-[12px]">No items — click "Add item" or upload an Excel to import.</div>
              )}
            </div>
          </TabsContent>
          <TabsContent value="ra" className="mt-4">
            <BOQRaList boqId={boqId} refresh={refreshTick => refreshTick} onChange={() => onSaved?.()} />
          </TabsContent>
          <TabsContent value="sections" className="mt-4">
            <BOQSectionsSummary boqId={boqId} boqItems={boq.items || []} />
          </TabsContent>
          <TabsContent value="recon" className="mt-4">
            <ReconciliationPanel rows={reconRows} boq={boq} />
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-slate-200 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          {boqId && boq?.original_file_id && (
            <>
              <Button
                variant="outline"
                onClick={() => window.open(`${api.defaults.baseURL}/boqs/${boqId}/original-file`, "_blank")}
                className="rounded-sm text-[11.5px]"
                data-testid="boq-download-original"
              >
                Download Original
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open(`${api.defaults.baseURL}/boqs/${boqId}/export-client-format`, "_blank")}
                className="rounded-sm text-[11.5px]"
                data-testid="boq-export-client-format"
              >
                Export · Client Format
              </Button>
              <Button
                variant="outline"
                onClick={() => setBulkOpen(true)}
                className="rounded-sm text-[11.5px] border-[#EA580C]/40 text-[#EA580C] hover:bg-orange-50"
                data-testid="boq-bulk-export"
                title="Zip up every RA bill of this BOQ in the client's exact Excel template"
              >
                <FileZip size={12} className="mr-1" /> Bulk Export (ZIP)
              </Button>
            </>
          )}
          {boqId && (
            <AbstractExportButton boqId={boqId} boq={boq} />
          )}
          <Button onClick={save} disabled={loading} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="boq-save"><FloppyDisk size={13} className="mr-1" /> {loading ? "Saving…" : "Save BOQ"}</Button>
        </div>
        {bulkOpen && (
          <BulkExportDialog
            boqId={boqId}
            boqNo={boq?.boq_no || ""}
            onClose={() => setBulkOpen(false)}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}


function BOQRaList({ boqId, onChange }) {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [comparing, setComparing] = useState(null);
  const load = useCallback(async () => {
    if (!boqId) return;
    try { const { data } = await api.get(`/ra-bills?boq_id=${boqId}`); setRows(data || []); } catch (e) { devLog.warn(e); }
  }, [boqId]);
  useEffect(() => { load(); }, [load]);

  const remove = async (r) => {
    if (!window.confirm(`Delete RA bill ${r.ra_bill_no}? This cannot be undone. Any linked measurement sheets will be unlinked. (Submitted / Paid bills cannot be deleted — cancel them instead.)`)) return;
    try {
      await api.delete(`/ra-bills/${r.id}`);
      toast.success(`Deleted ${r.ra_bill_no}`);
      load();
      onChange?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const cancelBill = async (r) => {
    if (!window.confirm(`Cancel RA bill ${r.ra_bill_no}? It will no longer count in cumulative billing, but stays visible in history for audit.`)) return;
    try {
      await api.post(`/ra-bills/${r.id}/status`, { status: "cancelled" });
      toast.success(`Cancelled ${r.ra_bill_no}`);
      load();
      onChange?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  if (!boqId) return <div className="text-[12.5px] text-slate-400">Save the BOQ first to create RA bills against it.</div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">RA bills · {rows.length}</div>
        <Button size="sm" className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white h-8" onClick={() => setCreating(true)} data-testid="ra-new-btn"><Plus size={13} className="mr-1" /> New RA Bill</Button>
      </div>
      {rows.length === 0 ? (
        <EmptyState title="No RA bills yet" description="Create RA-01 to start billing this BOQ." />
      ) : (
        <div className="border border-slate-200 rounded-sm overflow-hidden">
          <Table className="bc-list">
            <TableHeader><TableRow>
              <TableHead>RA #</TableHead><TableHead>Period</TableHead><TableHead>Date</TableHead>
              <TableHead className="text-right">This Bill</TableHead><TableHead className="text-right">Payable</TableHead>
              <TableHead>Status</TableHead><TableHead className="w-56"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => {
                const isLocked = r.status === "submitted" || r.status === "paid";
                return (
                <TableRow key={r.id} data-testid={`ra-row-${r.id}`}>
                  <TableCell className="font-mono font-semibold">{r.ra_bill_no}{r.is_final_bill ? " (FINAL)" : ""}</TableCell>
                  <TableCell className="text-[12px]">{r.bill_period_start ? fmtDate(r.bill_period_start) : "—"} → {r.bill_period_end ? fmtDate(r.bill_period_end) : "—"}</TableCell>
                  <TableCell className="text-[12px]">{r.bill_date ? fmtDate(r.bill_date) : "—"}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtMoney(r.gross_value_this_bill)}</TableCell>
                  <TableCell className="text-right font-mono-num font-bold text-emerald-700">{fmtMoney(r.amount_to_be_processed)}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" onClick={() => setEditing(r.id)} data-testid={`ra-open-${r.id}`}>Open</Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 rounded-sm text-[11px] border-[#0F172A]/30 text-[#0F172A] hover:bg-slate-100"
                        title="Side-by-side comparison of Original BOQ vs this RA Bill (editable)"
                        onClick={() => setComparing(r.id)}
                        data-testid={`ra-compare-${r.id}`}
                      >
                        <Scales size={11} className="mr-1" /> Compare
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" title="Download PDF" onClick={() => downloadWithToast(`/ra-bills/${r.id}/pdf`, `${r.ra_bill_no}.pdf`).catch(() => {})}><FilePdf size={11} /></Button>
                      <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" title="Download Excel" onClick={() => downloadWithToast(`/ra-bills/${r.id}/excel`, `${r.ra_bill_no}.xlsx`).catch(() => {})}><FileXls size={11} /></Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 rounded-sm text-[11px] border-[#EA580C]/40 text-[#EA580C] hover:bg-orange-50"
                        title="Download in client's original Excel format (with this RA bill overlaid)"
                        onClick={() => window.open(`${api.defaults.baseURL}/boqs/${r.boq_id}/export-client-format?ra_bill_id=${r.id}`, "_blank")}
                        data-testid={`ra-client-format-${r.id}`}
                      >
                        Client Format
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 rounded-sm text-[11px] border-[#EA580C]/40 text-[#EA580C] hover:bg-orange-50"
                        title="Client-format PDF (submission-ready)"
                        onClick={() => triggerFileDownload(`/boqs/${r.boq_id}/client-format-pdf?ra_bill_id=${r.id}`, `ClientFormat-${r.ra_bill_no}.pdf`)}
                        data-testid={`ra-client-pdf-${r.id}`}
                      >
                        <FilePdf size={11} className="mr-0.5" /> PDF
                      </Button>
                      {isLocked ? (
                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px] text-amber-700 border-amber-200 hover:bg-amber-50" title="Cancel this RA bill (keeps audit history)" onClick={() => cancelBill(r)} data-testid={`ra-cancel-${r.id}`}>Cancel</Button>
                      ) : (
                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px] text-red-600 border-red-200 hover:bg-red-50" title="Delete RA bill" onClick={() => remove(r)} data-testid={`ra-delete-${r.id}`}><Trash size={11} /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );})}
            </TableBody>
          </Table>
        </div>
      )}
      {creating && <RABillSheet boqId={boqId} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); onChange?.(); }} />}
      {editing && <RABillSheet raId={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); onChange?.(); }} />}
      {comparing && (
        <RABillComparisonSheet
          raId={comparing}
          onClose={() => setComparing(null)}
          onSaved={() => { load(); onChange?.(); }}
        />
      )}
    </div>
  );
}

function ReconciliationPanel({ rows, boq }) {
  if (!rows || rows.length === 0) return <div className="text-[12.5px] text-slate-400">No reconciliation data yet — save this BOQ, then add measurement sheets and challans.</div>;
  return (
    <div className="border border-slate-200 rounded-sm overflow-x-auto">
      <Table className="bc-list">
        <TableHeader><TableRow>
          <TableHead>SR</TableHead><TableHead>Description</TableHead><TableHead>Unit</TableHead>
          <TableHead className="text-right">BOQ Qty</TableHead>
          <TableHead className="text-right">Delivered</TableHead>
          <TableHead className="text-right">Measured</TableHead>
          <TableHead className="text-right">Billed (max)</TableHead>
          <TableHead>Flags</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.boq_item_index}>
              <TableCell className="font-mono text-[11.5px]">{r.sr_no || r.boq_item_index}</TableCell>
              <TableCell>{r.description}</TableCell>
              <TableCell className="uppercase text-[11px]">{r.unit}</TableCell>
              <TableCell className="text-right font-mono-num">{r.contract_qty}</TableCell>
              <TableCell className="text-right font-mono-num">{r.delivered_qty}</TableCell>
              <TableCell className="text-right font-mono-num">{r.measured_qty}</TableCell>
              <TableCell className="text-right font-mono-num font-semibold">{r.billed_qty}</TableCell>
              <TableCell>
                {r.flag_exceed_boq && <span className="inline-block text-[10px] uppercase font-semibold bg-red-100 text-red-700 rounded-sm px-1.5 py-[1px] mr-1">Billed &gt; BOQ</span>}
                {r.flag_delivered_over_boq && <span className="inline-block text-[10px] uppercase font-semibold bg-amber-100 text-amber-700 rounded-sm px-1.5 py-[1px] mr-1">Delivered &gt; BOQ</span>}
                {r.flag_measured_over_delivered && <span className="inline-block text-[10px] uppercase font-semibold bg-slate-100 text-slate-700 rounded-sm px-1.5 py-[1px] mr-1">Measured &gt; Delivered</span>}
                {!r.flag_exceed_boq && !r.flag_delivered_over_boq && !r.flag_measured_over_delivered && <span className="inline-block text-[10px] text-emerald-700"><CheckCircle size={11} className="inline mr-0.5" /> OK</span>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ---- BOQ Upload Dialog ----
function BOQUploadDialog({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [client, setClient] = useState("");
  const [busy, setBusy] = useState(false);
  const [diag, setDiag] = useState(null);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [aiReview, setAiReview] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const upload = async () => {
    if (!file) return toast.error("Pick a file first");
    setBusy(true);
    setDiag(null);
    setAiReview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("boq_title", title);
      fd.append("client_name", client);
      const { data } = await api.post("/boqs/upload-excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!(data.items || []).length) {
        setDiag({
          message: "Parser found 0 items — check that the sheet has a proper header row.",
          sheet_used: data.sheet_used,
          sheets_found: data.sheets_found,
          detected_columns: data.detected_columns,
          header_row: data.header_row,
        });
        toast.warning(`0 items found in sheet "${data.sheet_used}" — see diagnostics below.`);
        return;
      }
      const skippedN = (data.skipped_total_rows || []).length;

      let finalItems = data.items || [];
      let finalAi = null;

      if (aiEnabled) {
        setAiBusy(true);
        try {
          const { data: aiData } = await api.post("/boqs/ai-review", {
            items: data.items || [],
            skipped_rows: data.skipped_total_rows || [],
            raw_excerpt: data.raw_excerpt || "",
            sheet_name: data.sheet_used || "",
            header_row: data.header_row || 0,
          }, { timeout: 60000 });
          finalAi = aiData.ai_review || null;
          if (aiData.items) finalItems = aiData.items;
          setAiReview(finalAi);
        } catch (e) {
          setAiReview({ status: "skipped", reason: `network_error: ${e.message}` });
        } finally {
          setAiBusy(false);
        }
      }

      let aiExtra = "";
      if (finalAi && finalAi.status === "clean") aiExtra = " · AI verified ✓";
      else if (finalAi && (finalAi.issues || []).length) aiExtra = ` · AI found ${(finalAi.issues || []).length} issue(s)`;
      toast.success(`Parsed ${finalItems.length} item(s) from "${data.sheet_used}" · header row ${data.header_row}${skippedN ? ` · ${skippedN} total row(s) auto-removed` : ""}${aiExtra}. Review and save.`);
      onImported?.({
        boq_title: title || data.header?.boq_title || "",
        client_name: client || data.header?.client_name || "",
        site: data.header?.site || "",
        name_of_work: data.header?.name_of_work || "",
        sub_project: data.header?.sub_project || "",
        sub_head: data.header?.sub_head || "MEP",
        agreement_no: data.header?.agreement_no || "",
        work_order_no: data.header?.work_order_no || "",
        contractor_name: data.header?.contractor_name || "Celsius",
        items: finalItems,
        default_supply_pct: 85, default_install_pct: 10, default_tnc_pct: 5,
        retention_pct: 5, tds_pct: 1, default_gst_rate: 18,
      });
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setDiag({ message: msg });
      toast.error(msg);
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="boq-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Upload BOQ Excel</DialogTitle>
          <DialogDescription>The parser auto-detects sheets containing <b>Description · Qty · Rate</b> columns — supports "ABSTRACT", "BOQ", "Bill of Quantities", "Schedule" or any sheet with those headers. Accepts .xlsx (save your .xls as xlsx first).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="BOQ Title (optional)"><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. CP-III Orchard 16 Flat" data-testid="upload-title" /></Field>
          <Field label="Client (optional — read from Excel if blank)"><Input value={client} onChange={(e) => setClient(e.target.value)} data-testid="upload-client" /></Field>
          <Field label="Excel file (*.xlsx)">
            <Input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="upload-file" />
          </Field>
          <label className="flex items-center gap-2 text-[11.5px] text-slate-700 select-none cursor-pointer" data-testid="upload-ai-toggle-label">
            <input
              type="checkbox"
              className="accent-[#EA580C]"
              checked={aiEnabled}
              onChange={(e) => setAiEnabled(e.target.checked)}
              data-testid="upload-ai-toggle"
            />
            <span><b>AI-monitored parse</b> — Gemini audits every row for missed items, wrong section grouping, and non-default payment splits. Recommended for client BOQs.</span>
          </label>
          {aiBusy && (
            <div className="border border-orange-200 bg-orange-50 rounded-sm p-2.5 text-[11px] text-orange-900 flex items-center gap-2" data-testid="upload-ai-busy">
              <Warning size={12} className="animate-pulse" />
              AI Auditor running… Gemini reviewing every row (usually 15-25s)
            </div>
          )}
          {aiReview && (
            <div
              className={`border rounded-sm p-2.5 text-[11px] ${aiReviewCls(aiReview.status)}`}
              data-testid="upload-ai-review"
            >
              <div className="font-semibold flex items-center flex-wrap gap-x-2 gap-y-0.5">
                <Warning size={12} />
                {aiReview.status === "clean" && "AI Verified — no issues"}
                {aiReview.status === "skipped" && `AI Skipped${aiReview.reason ? ` (${aiReview.reason})` : ""}`}
                {aiReview.status !== "clean" && aiReview.status !== "skipped" && `AI ${aiReview.status} — ${(aiReview.issues || []).length} issue(s)`}
                {typeof aiReview.confidence === "number" && (
                  <span className="text-[10px] font-mono rounded-sm bg-white/60 border border-current/10 px-1.5 py-0.5">
                    {aiReview.confidence}% conf
                  </span>
                )}
              </div>
              {aiReview.summary && <div className="opacity-80 mt-0.5">{aiReview.summary}</div>}
            </div>
          )}
          {diag && (
            <div className="border border-amber-200 bg-amber-50 rounded-sm p-3 text-[11.5px] text-slate-700 space-y-1" data-testid="boq-upload-diag">
              <div className="font-semibold text-amber-800 flex items-center gap-1"><Warning size={12} /> {diag.message}</div>
              {diag.sheets_found && <div><span className="font-mono text-slate-500">sheets:</span> {diag.sheets_found.join(", ")}</div>}
              {diag.sheet_used && <div><span className="font-mono text-slate-500">parsed sheet:</span> {diag.sheet_used}</div>}
              {typeof diag.header_row === "number" && <div><span className="font-mono text-slate-500">header row:</span> {diag.header_row}</div>}
              {diag.detected_columns && <div><span className="font-mono text-slate-500">columns:</span> {JSON.stringify(diag.detected_columns)}</div>}
              <div className="text-slate-500 pt-1">Tip: ensure your sheet has a row with column labels like "Description", "Qty" and "Rate". If your header uses different wording (e.g. "Particulars", "Quantity", "Unit Price"), those are supported too.</div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
            <Button onClick={upload} disabled={busy || aiBusy || !file} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="upload-parse">
              {(() => {
                if (busy) return "Parsing…";
                if (aiBusy) return "Auditing…";
                return "Parse & Preview →";
              })()}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function aiReviewCls(status) {
  if (status === "clean") return "border-emerald-200 bg-emerald-50 text-emerald-900";
  if (status === "skipped") return "border-slate-200 bg-slate-50 text-slate-600";
  if (status === "errors") return "border-red-200 bg-red-50 text-red-900";
  return "border-amber-200 bg-amber-50 text-amber-900";
}

function MSPanel({ refreshTick }) {
  const [rows, setRows] = useState([]);
  const [boqs, setBoqs] = useState([]);
  const [creating, setCreating] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/measurement-sheets"); setRows(data || []); } catch (e) { devLog.warn(e); }
    try { const { data } = await api.get("/boqs"); setBoqs(data || []); } catch (e) { devLog.warn(e); }
  }, []);
  useEffect(() => { load(); }, [load, refreshTick]);

  const remove = async (id) => {
    if (!window.confirm("Delete this measurement sheet?")) return;
    try { await api.delete(`/measurement-sheets/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">Signed measurement sheets · {rows.length}</div>
        <Button size="sm" className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white h-8" onClick={() => setCreating(true)} data-testid="ms-new-btn"><Plus size={13} className="mr-1" /> New Measurement Sheet</Button>
      </div>
      {rows.length === 0 ? (
        <EmptyState title="No measurement sheets" description="Upload a signed measurement sheet + record measured qty per BOQ item to feed RA bills." />
      ) : (
        <div className="border border-slate-200 rounded-sm overflow-hidden">
          <Table className="bc-list">
            <TableHeader><TableRow>
              <TableHead>Sheet #</TableHead><TableHead>BOQ</TableHead><TableHead>Date</TableHead>
              <TableHead className="text-center">Signed</TableHead>
              <TableHead className="text-right">Value</TableHead><TableHead className="text-center">Lines</TableHead>
              <TableHead className="text-center">Linked RA</TableHead>
              <TableHead className="w-24"></TableHead>
            </TableRow></TableHeader>
            <TableBody>{rows.map((r) => {
              const boq = boqs.find((b) => b.id === r.boq_id);
              return (
                <TableRow key={r.id} data-testid={`ms-row-${r.id}`}>
                  <TableCell className="font-mono font-semibold">{r.sheet_no}</TableCell>
                  <TableCell>{boq?.boq_no} · {boq?.boq_title}</TableCell>
                  <TableCell>{r.sheet_date ? fmtDate(r.sheet_date) : "—"}</TableCell>
                  <TableCell className="text-center">{r.signed_by_client ? <span className="text-[10px] uppercase font-semibold bg-emerald-100 text-emerald-700 rounded-sm px-1.5 py-[1px]">✓ signed</span> : <span className="text-[10px] uppercase text-slate-400">unsigned</span>}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtMoney(r.total_measured_value)}</TableCell>
                  <TableCell className="text-center font-mono-num">{(r.lines || []).length}</TableCell>
                  <TableCell className="text-center text-[11px] font-mono">{r.linked_ra_bill_id ? "yes" : "—"}</TableCell>
                  <TableCell><Button size="sm" variant="outline" className="h-7 rounded-sm text-red-600 text-[11px]" onClick={() => remove(r.id)}><Trash size={11} /></Button></TableCell>
                </TableRow>
              );
            })}</TableBody>
          </Table>
        </div>
      )}
      {creating && <MSCreateDialog boqs={boqs} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

// ============================================================================
// Panel: Client Delivery Challans
// ============================================================================
function ChallanPanel({ refreshTick }) {
  const [rows, setRows] = useState([]);
  const [boqs, setBoqs] = useState([]);
  const [creating, setCreating] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/client-challans"); setRows(data || []); } catch (e) { devLog.warn(e); }
    try { const { data } = await api.get("/boqs"); setBoqs(data || []); } catch (e) { devLog.warn(e); }
  }, []);
  useEffect(() => { load(); }, [load, refreshTick]);

  const remove = async (id) => {
    if (!window.confirm("Delete this challan?")) return;
    try { await api.delete(`/client-challans/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">Client delivery challans · {rows.length}</div>
        <Button size="sm" className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white h-8" onClick={() => setCreating(true)} data-testid="dc-new-btn"><Plus size={13} className="mr-1" /> New Challan</Button>
      </div>
      {rows.length === 0 ? (
        <EmptyState title="No client-signed challans yet" description="Record delivered items + upload signed challans as proof for RA bill submissions." />
      ) : (
        <div className="border border-slate-200 rounded-sm overflow-hidden">
          <Table className="bc-list">
            <TableHeader><TableRow>
              <TableHead>Challan #</TableHead><TableHead>BOQ</TableHead><TableHead>Date</TableHead>
              <TableHead className="text-center">Signed</TableHead>
              <TableHead>Received by</TableHead><TableHead>Vehicle</TableHead>
              <TableHead className="text-center">Lines</TableHead>
              <TableHead className="w-24"></TableHead>
            </TableRow></TableHeader>
            <TableBody>{rows.map((r) => {
              const boq = boqs.find((b) => b.id === r.boq_id);
              return (
                <TableRow key={r.id} data-testid={`dc-row-${r.id}`}>
                  <TableCell className="font-mono font-semibold">{r.challan_no}</TableCell>
                  <TableCell>{boq?.boq_no} · {boq?.boq_title}</TableCell>
                  <TableCell>{r.challan_date ? fmtDate(r.challan_date) : "—"}</TableCell>
                  <TableCell className="text-center">{r.signed_by_client ? <span className="text-[10px] uppercase font-semibold bg-emerald-100 text-emerald-700 rounded-sm px-1.5 py-[1px]">✓ signed</span> : <span className="text-[10px] uppercase text-slate-400">unsigned</span>}</TableCell>
                  <TableCell>{r.received_by || "—"}</TableCell>
                  <TableCell className="font-mono text-[11.5px]">{r.vehicle_no || "—"}</TableCell>
                  <TableCell className="text-center font-mono-num">{(r.lines || []).length}</TableCell>
                  <TableCell><Button size="sm" variant="outline" className="h-7 rounded-sm text-red-600 text-[11px]" onClick={() => remove(r.id)}><Trash size={11} /></Button></TableCell>
                </TableRow>
              );
            })}</TableBody>
          </Table>
        </div>
      )}
      {creating && <ChallanCreateDialog boqs={boqs} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

// ============================================================================
// Panel: RA Bills (all)
// ============================================================================
function RABillsPanel({ onChange, refreshTick }) {
  const [rows, setRows] = useState([]);
  const [boqs, setBoqs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [comparing, setComparing] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/ra-bills"); setRows(data || []); } catch (e) { devLog.warn(e); }
    try { const { data } = await api.get("/boqs"); setBoqs(data || []); } catch (e) { devLog.warn(e); }
  }, []);
  useEffect(() => { load(); }, [load, refreshTick]);

  const remove = async (r) => {
    if (!window.confirm(`Delete RA bill ${r.ra_bill_no}? This cannot be undone. Any linked measurement sheets will be unlinked. (Submitted / Paid bills cannot be deleted — cancel them instead.)`)) return;
    try { await api.delete(`/ra-bills/${r.id}`); toast.success(`Deleted ${r.ra_bill_no}`); load(); onChange?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const cancelBill = async (r) => {
    if (!window.confirm(`Cancel RA bill ${r.ra_bill_no}? It will no longer count in cumulative billing but stays for audit.`)) return;
    try { await api.post(`/ra-bills/${r.id}/status`, { status: "cancelled" }); toast.success(`Cancelled ${r.ra_bill_no}`); load(); onChange?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <div className="space-y-3">
      <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">All RA bills · {rows.length}</div>
      {rows.length === 0 ? (
        <EmptyState title="No RA bills yet" description="Create a BOQ first, then bill it via RA-01, RA-02, ... " />
      ) : (
        <div className="border border-slate-200 rounded-sm overflow-hidden">
          <Table className="bc-list">
            <TableHeader><TableRow>
              <TableHead>RA #</TableHead><TableHead>BOQ</TableHead><TableHead>Bill Date</TableHead>
              <TableHead className="text-right">This Bill</TableHead><TableHead className="text-right">Payable</TableHead>
              <TableHead>Status</TableHead><TableHead className="w-56"></TableHead>
            </TableRow></TableHeader>
            <TableBody>{rows.map((r) => {
              const boq = boqs.find((b) => b.id === r.boq_id);
              const isLocked = r.status === "submitted" || r.status === "paid";
              return (
                <TableRow key={r.id} data-testid={`ra-list-row-${r.id}`}>
                  <TableCell className="font-mono font-semibold">{r.ra_bill_no}{r.is_final_bill ? " (FINAL)" : ""}</TableCell>
                  <TableCell>{boq?.boq_no} · {boq?.boq_title}</TableCell>
                  <TableCell>{r.bill_date ? fmtDate(r.bill_date) : "—"}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtMoney(r.gross_value_this_bill)}</TableCell>
                  <TableCell className="text-right font-mono-num font-bold text-emerald-700">{fmtMoney(r.amount_to_be_processed)}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" onClick={() => setEditing(r.id)} data-testid={`ra-list-open-${r.id}`}>Open</Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 rounded-sm text-[11px] border-[#0F172A]/30 text-[#0F172A] hover:bg-slate-100"
                        title="Side-by-side comparison of Original BOQ vs this RA Bill"
                        onClick={() => setComparing(r.id)}
                        data-testid={`ra-list-compare-${r.id}`}
                      >
                        <Scales size={11} className="mr-1" /> Compare
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" title="PDF" onClick={() => downloadWithToast(`/ra-bills/${r.id}/pdf`, `${r.ra_bill_no}.pdf`).catch(() => {})}><FilePdf size={11} /></Button>
                      <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" title="Excel" onClick={() => downloadWithToast(`/ra-bills/${r.id}/excel`, `${r.ra_bill_no}.xlsx`).catch(() => {})}><FileXls size={11} /></Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 rounded-sm text-[11px] border-[#EA580C]/40 text-[#EA580C] hover:bg-orange-50"
                        title="Client-format PDF (submission-ready)"
                        onClick={() => triggerFileDownload(`/boqs/${r.boq_id}/client-format-pdf?ra_bill_id=${r.id}`, `ClientFormat-${r.ra_bill_no}.pdf`)}
                        data-testid={`ra-list-client-pdf-${r.id}`}
                      >
                        Client PDF
                      </Button>
                      {isLocked ? (
                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px] text-amber-700 border-amber-200 hover:bg-amber-50" title="Cancel this RA bill (keeps audit history)" onClick={() => cancelBill(r)} data-testid={`ra-list-cancel-${r.id}`}>Cancel</Button>
                      ) : (
                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px] text-red-600 border-red-200 hover:bg-red-50" title="Delete RA bill" onClick={() => remove(r)} data-testid={`ra-list-delete-${r.id}`}><Trash size={11} /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}</TableBody>
          </Table>
        </div>
      )}
      {editing && <RABillSheet raId={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); onChange?.(); }} />}
      {comparing && (
        <RABillComparisonSheet
          raId={comparing}
          onClose={() => setComparing(null)}
          onSaved={() => { load(); onChange?.(); }}
        />
      )}
    </div>
  );
}
